import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import styles from "./WorkOrderDetails.module.scss";
import classNames from "classnames/bind";
import { IOverviewWorkOrderDetails } from "~/utils/interface/jobDetails";
import { useEffect, useState } from "react";
import jobDetailsApi from "~/apis/jobDetails";
import dayjs from "dayjs";
import { notify } from "~/utils/helper";
import { baseURL } from "~/utils/constants/env";
import logoImage from "~/assets/images/christian-brothers-roofing-logo.png";

const cx = classNames.bind(styles);

interface IWorkOrderDetailsProps {
  workOrderId: string;
  onEditWorkOrder: () => void;
}

const WorkOrderDetails = ({
  workOrderId,
  onEditWorkOrder,
}: IWorkOrderDetailsProps) => {
  const [workOrderDetails, setWorkOrderDetails] =
    useState<IOverviewWorkOrderDetails | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchWorkOrderDetails = async () => {
      try {
        const res = await jobDetailsApi.getOverviewWorkOrderDetails(
          workOrderId
        );
        setWorkOrderDetails(res.data.data);
      } catch (error) {
        console.error("Get Work Order Details failed:", error);
      }
    };
    fetchWorkOrderDetails();
  }, [workOrderId]);

  const formatFileSize = (size: string | number | undefined): string => {
    if (!size) return "";

    let bytes: number;
    if (typeof size === "string") {
      if (size.includes("KB") || size.includes("MB") || size.includes("GB")) {
        return size;
      }
      bytes = parseFloat(size);
    } else {
      bytes = size;
    }

    if (isNaN(bytes) || bytes === 0) return "";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Helper function to generate HTML from work order data
  const generateWorkOrderHTML = (): string => {
    if (!workOrderDetails) return "";

    const formatDate = (date: string | undefined) => {
      if (!date) return "N/A";
      return dayjs(date).format("MM/DD/YYYY");
    };

    // Get job data from work order details (backend includes it in response)
    const job = (workOrderDetails as any).job;
    console.log("baseURL>>>", baseURL)
    // Get sales rep info
    const salesRep = job?.salesRep;
    const salesRepName = salesRep?.profile
      ? `${salesRep.profile.firstName || ""} ${salesRep.profile.lastName || ""}`.trim() || salesRep?.username || "N/A"
      : salesRep?.username || "N/A";
    const salesRepPhone = salesRep?.profile?.phoneNumber || "N/A";
    const salesRepEmail = salesRep?.email || "N/A";

    // Get customer info (primary or secondary)
    const customer = job?.primaryCustomer || job?.secondaryCustomer;
    const customerName = customer?.name || "N/A";
    const customerPhone = customer?.primaryPhone || "N/A";
    const customerAddress = job?.jobAddress || customer?.addressLine1 || "N/A";
    const customerCityState = customer?.city && customer?.state
      ? `${customer.city}, ${customer.state} ${customer.postalCode || ""}`.trim()
      : "N/A";

    const laborRows = workOrderDetails.laborDetails
      .map(
        (labor) => `
        <tr>
          <td>${labor.perSquare || ""}</td>
          <td></td>
          <td>${Number(labor.qty || 0).toFixed(2)}</td>
        </tr>
      `
      )
      .join("");

    const imagesHTML = workOrderDetails.images
      .map((img) => {
        if (img.image?.url) {
          let imageUrl = img.image.url;
          if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
            imageUrl = imageUrl.startsWith("/") ? imageUrl : `/api/assets/${imageUrl}`;
          }
          return `<img src="${imageUrl}" alt="Work Order Image" />`;
        }
        return "";
      })
      .join("");

    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <link href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">
          <title>Work Order</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Montserrat', sans-serif;
              margin: 0;
              padding: 0;
              color: #000;
              font-size: 11px;
              line-height: 1.4;
            }
            .container { max-width: 8.5in; margin: 0 auto; padding: 0; }
            .header-bar {
              background-color: #000;
              color: #fff;
              text-align: center;
              padding: 8px 0;
              font-weight: bold;
              font-size: 18px;
              letter-spacing: 2px;
            }
            .top-section {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 15px 0;
            }
            .left-group { display: flex; gap: 30px; align-items: flex-start; }
            .company-info h2 { font-size: 16px; font-weight: bold; margin-bottom: 8px; }
            .company-info p { font-size: 11px; margin: 2px 0; }
            .logo-section { flex: 0 0 250px; text-align: right; }
            .logo-section img { max-height: 80px; max-width: 220px; }
            .sales-rep-info h3 { font-size: 12px; font-weight: bold; margin-bottom: 8px; }
            .sales-rep-info p { font-size: 11px; margin: 2px 0; }
            .customer-wo-section { display: flex; justify-content: space-between; padding: 15px 0; }
            .customer-info p { font-size: 11px; font-weight: bold; margin: 4px 0; }
            .wo-box { flex: 0 0 250px; padding: 10px; background-color: #fff; }
            .wo-box-row { display: flex; margin-bottom: 2px; }
            .wo-label {
              background-color: #e0e0e0;
              padding: 5px 10px;
              font-weight: bold;
              font-size: 10px;
              width: 120px;
              border: 1px solid #ccc;
              border-radius: 5px;
              margin-right: 2px;
            }
            .wo-value {
              padding: 5px 10px;
              font-size: 11px;
              flex: 1;
              background-color: #fff;
              border: 1px solid #ccc;
              border-radius: 5px;
            }
            .items-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 2px 2px;
              margin-top: 10px;
            }
            .items-table th {
              background-color: #e0e0e0;
              padding: 8px;
              text-align: left;
              font-weight: bold;
              font-size: 11px;
              border: 1px solid #ccc;
              border-radius: 5px;
            }
            .items-table td {
              padding: 8px;
              border: 1px solid #ccc;
              border-radius: 5px;
              vertical-align: top;
              font-size: 11px;
            }
            .images-grid {
              display: flex;
              gap: 15px;
              flex-wrap: wrap;
              margin-top: 10px;
            }
            .images-grid img {
              max-width: 200px;
              max-height: 200px;
              object-fit: contain;
              border: 1px solid #ddd;
              border-radius: 2px;
            }
            .special-instructions {
              padding: 20px;
              text-align: center;
            }
            .special-instructions h3 {
              font-size: 14px;
              font-weight: bold;
              letter-spacing: 3px;
              margin-bottom: 15px;
              text-transform: uppercase;
              border-bottom: 1px solid #ccc;
            }
            .special-instructions p {
              font-size: 11px;
              text-align: left;
              margin: 5px 0;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-bar">WORK ORDER</div>
            <div class="top-section">
              <div class="left-group">
                <div class="company-info">
                  <p>Christian Brothers Roofing</p>
                  <p>21617 N 2nd Ave Phoenix, AZ 85027</p>
                  <p>(623) 204-1476</p>
                  <p>ROC 273143 • ROC 297384</p>
                </div>
                <div class="sales-rep-info">
                  <h3>Sales Representative</h3>
                  <p>${salesRepName}</p>
                  <p>${salesRepPhone}</p>
                  <p>${salesRepEmail}</p>
                </div>
              </div>
              <div class="logo-section">
                <img src="${logoImage}" alt="Logo" />
              </div>
            </div>
            <div class="customer-wo-section">
              <div class="customer-info">
                <p>${customerName}</p>
                <p>${customerPhone}</p>
                <p>${customerAddress}</p>
                <p>${customerCityState}</p>
              </div>
              <div class="wo-box">
                <div class="wo-box-row">
                  <div class="wo-label">Work Order #</div>
                  <div class="wo-value">${workOrderDetails.workOrderId || "N/A"}</div>
                </div>
                <div class="wo-box-row">
                  <div class="wo-label">Date</div>
                  <div class="wo-value">${formatDate(workOrderDetails.createdAt)}</div>
                </div>
              </div>
            </div>
            <div class="items-section">
              <table class="items-table">
                <thead>
                  <tr>
                    <th style="width: 20%;">Item</th>
                    <th style="width: 60%;">Description</th>
                    <th style="width: 20%; text-align: center;">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  ${laborRows || "<tr><td></td><td></td><td></td></tr>"}
                </tbody>
              </table>
            </div>
            ${imagesHTML ? `<div class="images-section"><div class="images-grid">${imagesHTML}</div></div>` : ""}
            <div class="special-instructions">
              <h3>SPECIAL INSTRUCTIONS</h3>
              <p>${workOrderDetails.specialInstructions || "&nbsp;"}</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Helper function to generate PDF filename
  // Format: CustomerName---Address-Work-Order-#WorkOrderId-Type
  // Example: Ernie-Katsampes---20811-Work-Order-#4238-Roofing
  const generatePDFFilename = (): string => {
    if (!workOrderDetails) {
      return `work-order-${workOrderId}.pdf`;
    }

    try {
      // Get job data from work order details (backend includes it in response)
      const job = (workOrderDetails as any).job;
      
      let customerName = "";
      let addressNumber = "";
      
      if (job) {
        // Get customer name from primary or secondary customer
        const customer = job.primaryCustomer || job.secondaryCustomer;
        
        if (customer?.name) {
          // Replace spaces with hyphens and remove special characters
          customerName = customer.name
            .replace(/\s+/g, "-")
            .replace(/[^a-zA-Z0-9-]/g, "")
            .trim();
        }
        
        // Extract address number from job address or customer address
        const address = job.jobAddress || customer?.addressLine1 || "";
        if (address) {
          // Extract numbers from address (e.g., "20811 N 2nd Ave" -> "20811")
          const addressMatch = address.match(/^\d+/);
          if (addressMatch) {
            addressNumber = addressMatch[0];
          }
        }
      }

      // Build filename parts
      const parts: string[] = [];
      
      // Customer name
      if (customerName) {
        parts.push(customerName);
      }
      
      // Address number
      if (addressNumber) {
        parts.push(addressNumber);
      }
      
      // Work Order #
      const woNumber = workOrderDetails.workOrderId || workOrderId;
      if (woNumber) {
        parts.push(`Work-Order-#${woNumber}`);
      }
      
      // Type
      const type = workOrderDetails.type;
      if (type) {
        // Capitalize first letter and replace spaces with hyphens
        const formattedType = type
          .charAt(0).toUpperCase() + type.slice(1)
          .replace(/\s+/g, "-")
          .replace(/[^a-zA-Z0-9-]/g, "");
        parts.push(formattedType);
      }
      
      // Join with "---" separator
      const filename = parts.length > 0 
        ? parts.join("---") + ".pdf"
        : `work-order-${workOrderId}.pdf`;
      
      return filename;
    } catch (error) {
      console.error("Error generating filename:", error);
      return `work-order-${workOrderId}.pdf`;
    }
  };

  const handleDownloadPDF = async () => {
    if (!workOrderId || !workOrderDetails) {
      notify("error", "Work Order data not found");
      return;
    }

    setIsDownloading(true);
    try {
      // Generate PDF filename
      const filename = generatePDFFilename();
      
      // Generate HTML from work order data
      const html = generateWorkOrderHTML();
      
      if (!html) {
        notify("error", "Failed to generate HTML content");
        return;
      }

      // Send HTML to backend to render PDF
      const response = await jobDetailsApi.exportWorkOrderPDFFromHTML(html, {
        filename: filename,
      });
      
      // Create blob from response
      const blob = new Blob([response.data], { type: response.data.type || 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = filename;
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
      
      notify("success", "PDF downloaded successfully");
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  function convertTo12Hour(timeString: string) {
    const time = timeString.split("+")[0];
    const [hours, minutes] = time.split(":");

    const hour = parseInt(hours);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minutes} ${period}`;
  }

  return (
    <div className={cx("work-order-details-container")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Work Order Details</h2>

        <div className={cx("actions")}>
          <PrimaryButton
            title="Edit Work Order"
            onClick={onEditWorkOrder}
            className={cx("edit-button")}
          />

          {workOrderDetails && (
            <>
              <PrimaryButton
                title="Download PDF"
                onClick={handleDownloadPDF}
                className={cx("download-button")}
                loading={isDownloading}
                disabled={isDownloading}
              />
            </>
          )}
        </div>
      </div>

      <div className={cx("content")}>
        <div className={cx("info-container")}>
          <div className={cx("info-row")}>
            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Work Order ID</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.workOrderId}
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Start Date</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.startDate
                  ? dayjs(workOrderDetails.startDate).format("MMMM DD, YYYY")
                  : "-"}
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Start Time</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.startTime ? convertTo12Hour(workOrderDetails.startTime) : "-"}
              </div>
            </div>
          </div>

          <div className={cx("info-row")}>
            <div className={cx("info-item")}>
              <label className={cx("info-label")}>End Date</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.endDate
                  ? dayjs(workOrderDetails.endDate).format("MMMM DD, YYYY")
                  : "-"}
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>End Time</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.endTime
                  ? convertTo12Hour(workOrderDetails.endTime)
                  : "-"}
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Type</label>
              <div className={cx("info-value")}>{workOrderDetails?.type}</div>
            </div>
          </div>

          <div className={cx("info-row")}>
            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Status</label>
              <div className={cx("info-value")}>
                <span
                  className={cx("status", {
                    "badge-progress":
                      workOrderDetails?.status === "In Progress",
                    "badge-completed": workOrderDetails?.status === "Completed",
                    "badge-pending": workOrderDetails?.status === "Pending",
                  })}
                >
                  {workOrderDetails?.status}
                </span>
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Priority</label>
              <div className={cx("info-value")}>
                <span
                  className={cx("priority", {
                    "badge-high": workOrderDetails?.priority === "High",
                    "badge-normal": workOrderDetails?.priority === "Normal",
                    "badge-low": workOrderDetails?.priority === "Low",
                  })}
                >
                  {workOrderDetails?.priority}
                </span>
              </div>
            </div>

            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Estimated Duration</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.estimatedDuration
                  ? `${workOrderDetails.estimatedDuration?.replace(
                      ".00",
                      ""
                    )} hours`
                  : "-"}
              </div>
            </div>
          </div>

          <div className={cx("info-row")}>
            <div className={cx("info-item")}>
              <label className={cx("info-label")}>Due Date</label>
              <div className={cx("info-value")}>
                {workOrderDetails?.dueDate
                  ? dayjs(workOrderDetails.dueDate).format("MMMM DD, YYYY")
                  : "-"}
              </div>
            </div>

            <div className={cx("info-item", "full-width")}>
              <label className={cx("info-label")}>
                Assigned Subcontractors
              </label>
              <div className={cx("info-value", "subcontractors")}>
                {workOrderDetails?.subcontractors.map((subcontractor) => (
                  <span className={cx("subcontractor", "badge-subcontractor")}>
                    {subcontractor.account?.username}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Labor Included Section */}
        <div className={cx("section")}>
          <h3 className={cx("section-title")}>Labor Included</h3>

          <div className={cx("table-container")}>
            <table className={cx("labor-table")}>
              <thead>
                <tr>
                  <th>Per Square</th>
                  <th>UOM</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Markup%</th>
                  <th>Line Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {workOrderDetails?.laborDetails.map((labor) => (
                  <tr key={labor.id}>
                    <td>{labor.perSquare}</td>
                    <td>{labor.uom}</td>
                    <td>{labor.qty}</td>
                    <td>${labor.price}</td>
                    <td>{labor.markupPercent}%</td>
                    <td>${labor.lineTotal}</td>
                    <td>
                      <button className={cx("delete-btn")}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Attached Images Section */}
        <div className={cx("section")}>
          <h3 className={cx("section-title")}>Attached Images</h3>

          {workOrderDetails?.images?.length &&
          workOrderDetails?.images?.length > 0 ? (
            <div className={cx("images-grid")}>
              {workOrderDetails?.images.map((image) => (
                <div key={image.id} className={cx("image-card")}>
                  <img
                    src={image.image?.url || ""}
                    alt={image.image?.fieldName || ""}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={cx("image-placeholder")}>No images found</div>
          )}
        </div>

        {/* Attachments Section */}
        <div className={cx("section")}>
          <h3 className={cx("section-title")}>Attachments</h3>

          {workOrderDetails?.attachments?.length &&
          workOrderDetails?.attachments?.length > 0 ? (
            <div className={cx("attachments-list")}>
              {workOrderDetails?.attachments.map((attachment) => (
                <div key={attachment.id} className={cx("attachment-item")}>
                  <div className={cx("attachment-info")}>
                    <span className={cx("attachment-name")}>
                      {attachment.file?.fieldName ||
                        attachment.file?.url ||
                        "Attachment"}
                    </span>
                    {attachment.file?.size && (
                      <span className={cx("attachment-size")}>
                        {formatFileSize(attachment.file.size)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={cx("attachment-placeholder")}>
              No attachments found
            </div>
          )}
        </div>

        {/* Special Instructions and Internal Notes */}
        <div className={cx("notes-section")}>
          <div className={cx("notes-column")}>
            <h3 className={cx("notes-title")}>Special Instructions</h3>
            <div className={cx("notes-content")}>
              {workOrderDetails?.specialInstructions || "-"}
            </div>
          </div>

          <div className={cx("notes-column")}>
            <h3 className={cx("notes-title")}>Internal Notes</h3>
            <div className={cx("notes-content")}>
              {workOrderDetails?.internalNote || "-"}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className={cx("summary-section")}>
          <h3 className={cx("summary-title")}>Summary</h3>

          <div className={cx("summary-content")}>
            <div className={cx("summary-row")}>
              <span className={cx("summary-label")}>Material Cost:</span>
              <span className={cx("summary-value")}>
                ${workOrderDetails?.totalMaterialCost || "-"}
              </span>
            </div>
            <div className={cx("summary-row")}>
              <span className={cx("summary-label")}>Labor Cost:</span>
              <span className={cx("summary-value")}>
                ${workOrderDetails?.totalLaborCost || "-"}
              </span>
            </div>
            <div className={cx("summary-row")}>
              <span className={cx("summary-label")}>Total Cost:</span>
              <span className={cx("summary-value")}>
                ${workOrderDetails?.totalCost || "-"}
              </span>
            </div>
            <div className={cx("summary-row")}>
              <span className={cx("summary-label")}>Margin:</span>
              <span className={cx("summary-value")}>
                {workOrderDetails?.marginPercent
                  ? `${workOrderDetails.marginPercent}%`
                  : "-"}
              </span>
            </div>
            <div className={cx("summary-row", "total-row")}>
              <span className={cx("summary-label")}>Total Price:</span>
              <span className={cx("summary-value", "total-price")}>
                ${workOrderDetails?.totalPrice || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Activity Log Section */}
        <div className={cx("section")}>
          <h3 className={cx("section-title")}>Activity Log</h3>

          {workOrderDetails?.activityLogs.length &&
          workOrderDetails?.activityLogs.length > 0 ? (
            <div className={cx("activity-log")}>
              {workOrderDetails?.activityLogs.map((activity) => (
                <div className={cx("activity-item")}>
                  <div className={cx("activity-time")}>
                    <div className={cx("activity-date")}>
                      {dayjs(activity.createdAt).format("YYYY-MM-DD")}
                    </div>
                    <div className={cx("activity-timestamp")}>
                      {dayjs(activity.createdAt).format("HH:mm A")}
                    </div>
                    <div className={cx("activity-user")}>
                      {activity.actionByAccount?.profile?.firstName ||
                      activity.actionByAccount?.profile?.lastName
                        ? `${
                            activity.actionByAccount?.profile?.firstName || ""
                          } ${
                            activity.actionByAccount?.profile?.lastName || ""
                          }`
                        : "-"}
                    </div>
                  </div>
                  <div className={cx("activity-description")}>
                    {activity.description || "-"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={cx("activity-placeholder")}>No activity found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetails;
